import { useCallback, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { omit } from 'lodash';
import { toast } from '@/components/hooks/use-toast';
import { fetchClientByClientIdResponse, selectClient } from '@client/model';
import { guidedPlanningApi } from '@/features/guided-financial-planning/api';
import { useSession, useSessionMutations } from '@/features/guided-financial-planning/context';
import {
  buildDeductionRestorations,
  isSamePolicy,
  listSelectedQuotePolicies,
} from '@/features/guided-financial-planning/utils/finalized-policies';

/**
 * Reopens a finalized session when the agent edits it, undoing what finalizing did to the client so
 * finalizing again doesn't repeat it:
 * 1. gives back what finalizing deducted from each funded account;
 * 2. puts the session back in progress, dropping the finalization snapshots so figures recalculate,
 *    and records the policies finalizing saved. Those stay on the client until the session is
 *    finalized again, which keeps or replaces them (`useReplaceFinalizedPolicies`).
 *
 * `reopen(stepData)` writes `stepData` in the same request, for the edit that triggered it, and
 * resolves to whether the session is now editable.
 */
export function useReopenSession() {
  const dispatch = useDispatch();
  const client = useSelector(selectClient);
  const { session } = useSession();
  const { reopenSession } = useSessionMutations();
  const [isReopening, setIsReopening] = useState(false);
  const inFlight = useRef<Promise<boolean> | null>(null);

  const reopen = useCallback(
    (stepData: GuidedPlanningStepData = {}): Promise<boolean> => {
      if (inFlight.current) return inFlight.current;
      if (!client?.clientId || session?.status !== 'completed') return Promise.resolve(true);

      const clientId = client.clientId;
      const currentSelfFunding = client.multipleFundingSources?.selfFunding;
      const restorations = buildDeductionRestorations(session.stepData, currentSelfFunding);
      const savedPolicies = listSelectedQuotePolicies(
        session.stepData?.selectedQuotes,
        clientId,
        client.mutableClientPartner?.clientId,
      );
      const earlierPolicies = (session.stepData?.previouslyFinalizedPolicies ?? []).filter(
        ref => !savedPolicies.some(saved => isSamePolicy(saved, ref)),
      );
      const reopenedStepData: GuidedPlanningStepData = {
        ...omit(session.stepData ?? {}, ['planComparisonSnapshot', 'multipleFundingSourcesSnapshot']),
        previouslyFinalizedPolicies: [...earlierPolicies, ...savedPolicies],
        ...stepData,
      };

      const run = async () => {
        setIsReopening(true);
        let restored = false;
        try {
          if (restorations.length > 0) {
            const clientContainer = await guidedPlanningApi.batchUpdateSelfFundingAssets({
              clientId,
              updates: restorations,
            });
            restored = true;
            dispatch(fetchClientByClientIdResponse(clientContainer));
          }
          await reopenSession(reopenedStepData);
          return true;
        } catch {
          // Keep "finalized" and "deducted" together: a session left finalized must keep its deductions.
          if (restored) {
            const clientContainer = await guidedPlanningApi
              .batchUpdateSelfFundingAssets({
                clientId,
                updates: restorations.map(({ fundingSourceId }) => ({
                  fundingSourceId,
                  existingAssetsValue: currentSelfFunding?.[fundingSourceId]?.existingAssetsValue ?? 0,
                })),
              })
              .catch(() => null);
            if (clientContainer) dispatch(fetchClientByClientIdResponse(clientContainer));
          }
          toast({
            variant: 'destructive',
            title: 'We couldn’t reopen this plan',
            description: 'Your change wasn’t saved. Please try again.',
          });
          return false;
        } finally {
          setIsReopening(false);
          inFlight.current = null;
        }
      };

      inFlight.current = run();
      return inFlight.current;
    },
    [client, session, reopenSession, dispatch],
  );

  return { reopen, isReopening };
}
